import { OwnApartment } from '@models/apartment.types';
import { Facility, FacilityType } from '@models/realEstate.types';
import { TranslatorFn } from '@stores/localization';

import { isLocalOrTestEnv } from './featureFlags';

export const isFeature =
  <FacilityT extends FacilityType>(...features: FacilityT[]) =>
  <T extends Facility>(instance: T): instance is { feature: FacilityT } & T =>
    'feature' in instance &&
    features.some((feature) => feature === instance.feature);

/** Match some selected keywords from facility title and translate */
export const translateFeatureLocationTitle = (title: string, t: TranslatorFn) =>
  title
    .replace(/(asuinkerrostalo)/i, t('apartment_building_keyword'))
    .replace(/(asuinrivitalo)/i, t('apartment_row_house_keyword'))
    .replace(/(varastorakennus)/i, t('storage_house_keyword'))
    .replace(/(kerros)/i, t('floor_keyword'))
    .replace(/(muu)/i, t('other_keyword'));

export const getFeatureLocationTitle = (
  location: Facility['location'],
  t: TranslatorFn
) => {
  if (!location) {
    if (isLocalOrTestEnv)
      console.warn(
        '[getFeatureLocationTitle] There is a Facility without a location.'
      );

    return '';
  }
  const { floorName, stairwellName, buildingName, realestateName } = location;

  return translateFeatureLocationTitle(
    floorName ?? stairwellName ?? buildingName ?? realestateName,
    t
  );
};

/** Concatenate and format a list of facilities to a readable string */
export const formatFacilitiesTitle = (
  facilities: Facility[] | undefined,
  t: TranslatorFn
) =>
  facilities
    ?.map(({ location }) => getFeatureLocationTitle(location, t))
    .join(', ');

/** Re-structure relevant apartment properties as an object */
export const parseApartmentProperties = ({
  realestate,
  laundryHouseReservationUrl,
}: OwnApartment) => {
  const parties = realestate.parties;
  const facilities = realestate.realEstateFeatureLocations;

  return {
    hasParking: !!parties.PARKINGSPACE_RENTAL_COMPANY?.length,
    maintenanceCompany: parties.MAINTENANCE_COMPANY?.[0],
    parkingCompany: parties.PARKINGSPACE_RENTAL_COMPANY?.[0],
    houseExpert: parties.SUPERINTENDENT?.[0],
    housingManager:
      parties.HOUSING_MANAGER?.[0] ?? parties.HOUSING_MANAGER_COMPANY?.[0],
    serviceManager: parties.SERVICESUPERIOR?.[0],
    keyManagementCompany:
      parties.KEYS_MANAGEMENT?.[0] ?? parties.MAINTENANCE_COMPANY?.[0],
    parkingSpaceKeyManagementCompany: parties.PARKINGSPACE_KEY_MANAGEMENT?.[0],
    laundryRooms: facilities.filter(
      isFeature('LAUNDRY_HOUSE', 'LAUNDRY_HOUSE_RENTABLE')
    ),
    clubHouses: facilities.filter(isFeature('CLUBHOUSE')),
    shelters: facilities.filter(isFeature('SHELTER')),
    outdoorStorageSpaces: facilities.filter(isFeature('OUTDOOR_STORAGE_ROOM')),
    babyCarriageStorages: facilities.filter(
      isFeature('BABY_CARRIAGE_STORAGE_ROOM')
    ),
    dryingRooms: facilities.filter(isFeature('DRYING_ROOM')),
    saunas: facilities.filter(isFeature('REAL_ESTATE_SAUNA')),
    storageSpaces: facilities.filter(
      isFeature('ATTIC_OR_CELLAR_OR_STORAGE_ROOM')
    ),
    laundryRoomReservationURL: laundryHouseReservationUrl ?? undefined,
    parcelMachine: realestate?.parcelMachine,
  };
};

export type ApartmentProperties = ReturnType<typeof parseApartmentProperties>;
